import React, { useContext, useEffect, useState } from 'react';

import { createStyles, makeStyles, Theme, withStyles } from '@material-ui/core/styles';
import { GridRowParams, GridValueFormatterParams } from '@mui/x-data-grid';

import CustomDataGrid from '../../components/data-grid';
import Screen from '../../components/screen';
import SplashWait from '../../components/splash-wait';
import { AppContext } from '../../contexts/app-context';
import GlobalStyles from '../../global-styles';
import API from '../../utils/api';
import {
  apiReponseError, apiRequestError, canReadContractOrder, canWriteContractOrder,
  checkUserAuthenticated, formatDateForDataGrid, getContractOrders, getShowDashboardValue,
  goToRoutePage, openExternalLink, setWindowTitle, toggleShowDashboard
} from '../../utils/helper';
import {
  AddIcon, DashboardOffIcon, DashboardOnIcon, GetAppToneIcon, HelpIcon, HideAllIcon, MoreIcon,
  RefreshIcon, ShowAllIcon
} from '../../utils/icons';
import {
  Box, Breadcrumbs, Button, Card, CardContent, CardHeader, FormControl, Grid, Hidden, IconButton,
  InputLabel, Link, ListItemIcon, ListItemText, Menu, MenuItem, Select, TextField, Tooltip,
  Typography
} from '../../utils/material-ui-core';

interface SearchCriteria { clientCompanyKey: string, statusId: number, lockedId: number, contractOrderNumber: string, programCode: string, accountStatus: string, accountNumber: string, equipmentVendorId: number, postingStatusId: number, expiredIn: string }

const defaultSearchCriteriaValue = { clientCompanyKey: "", statusId: 0, lockedId: 0, contractOrderNumber: "", programCode: "", accountStatus: "", accountNumber: "", equipmentVendorId: -1, postingStatusId: -1, expiredIn: "" } as SearchCriteria;

const ManageContractOrders = () => {
  const pageStyles = LocalPageStyles();
  const globalStyles = GlobalStyles();

  const { isUserAuthenticated, userFunctionAccess, showAlert, referenceLookups } = useContext(AppContext);

  const [anchorElForOptionsMenu, setAnchorElForOptionsMenu] = useState<null | HTMLElement>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [showDashboard, setShowDashboard] = useState<boolean>(true);
  const [showFilterOptions, setShowFilterOptions] = useState<boolean>(false);
  const [searchCriteria, setSearchCriteria] = useState<SearchCriteria>(defaultSearchCriteriaValue)
  const [contractOrders, setContractOrders] = useState<ContractOrder[]>([]);
  const [filterContractOrders, setFilterContractOrders] = useState<ContractOrder[]>([]);
  const [programs, setPrograms] = useState<ContractOrderProgram[]>([]);
  const [issueSummaries, setIssueSummaries] = useState<ContractOrderIssueSummaryItem[]>([]);
  const [preOrderStatusSummaries, setPreOrderStatusSummaries] = useState<TitleCountSummaryItem[]>([]);
  const [statusSummaries, setStatusSummaries] = useState<TitleCountIdSummaryItem[]>([]);
  const [lookupValues, setLookupValues] = useState<ContractOrderLookupValues>({ orderStatus: [], contractType: [], invoiceStatus: [], postingStatus: [], vendors: [] });

  const columns = [
    { field: "shortDescription", headerName: "Short Description", width: 300, hide: true },
    { field: "longDescription", headerName: "Long Description", width: 300, hide: true },
    { field: "contractOrderNumber", headerName: "Contract Order Number", width: 400 },
    { field: "lockedIndicator", headerName: "Locked", width: 130, type: "boolean" },
    { field: "status", headerName: "Status", width: 130 },
    { field: "clientCompanyName", headerName: "Client Company Name", width: 300 },
    { field: "clientCompanyCode", headerName: "Client Company Code", width: 250, hide: true },
    { field: "clientCompanyShortName", headerName: "Contract Order Prefix", width: 250, hide: true },
    { field: "equipmentVendor", headerName: "Equipment Vendor", width: 200, hide: true },
    { field: "masterAgreementLevel", headerName: "Master Agreement Level", width: 300, hide: true },
    { field: "orderNumber", headerName: "Order Number", width: 200, hide: true },
    { field: "postingStatus", headerName: "Posting Status", width: 200, hide: true },
    { field: "invoiceStatus", headerName: "Invoice Status", width: 200, hide: true },
    { field: "contractType", headerName: "Contract Type", width: 200, hide: true },
    { field: "override", headerName: "Contract Order Suffix", width: 250, hide: true },
    { field: "taxExempt", headerName: "taxExempt", width: 200, type: "boolean", hide: true },
    { field: "effectiveDate", headerName: "Effective Date", width: 200, type: "date", valueFormatter: (params: GridValueFormatterParams) => formatDateForDataGrid(params.value as string) },
    { field: "expirationDate", headerName: "Expiration Date", width: 200, type: "date", valueFormatter: (params: GridValueFormatterParams) => formatDateForDataGrid(params.value as string), hide: true },
    { field: "expiredIn", headerName: "Expired In", width: 200, hide: false },
    { field: "programCount", headerName: "No Of Programs Included", width: 250, type: "number", hide: false },
    { field: "programsDescription", headerName: "Programs", width: 500, hide: true },
    { field: "accountCount", headerName: "No Of Accounts Included", width: 250, type: "number", hide: false },
    { field: "accountsDescription", headerName: "Accounts", width: 500, hide: true },
    { field: "accountValidStatus", headerName: "Account Exists", width: 200 },
    { field: "folderId", headerName: "Folder Id", width: 200, hide: true },
    { field: "orderCount", headerName: "Order #", width: 200 },
    { field: "lastModifiedBy", headerName: "Last Modified By", width: 300, hide: true },
    { field: "lastModifiedDateTime", headerName: "Last Modified Date", width: 250, type: "dateTime", valueFormatter: (params: GridValueFormatterParams) => formatDateForDataGrid(params.value as string) },
    { field: "contractOrderKey", headerName: "Key", width: 300, hide: true }
  ];

  const tempProgams: ContractOrderProgram[] = [];

  // Options menu related func
  const handleAnchorElOptionsMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElForOptionsMenu(event.currentTarget);
  };

  const handleAnchorElOptionsMenuClose = () => {
    setAnchorElForOptionsMenu(null);
  };

  // Search criteria changed
  const searchCriteriaChanged = (data: ContractOrder[], criteria: SearchCriteria) => {

    let filterRows = data;
    let isDataFiltered = false;

    window.localStorage.setItem("ContractOrderSerachForm", JSON.stringify(criteria));

    if (criteria.clientCompanyKey !== "") {
      filterRows = filterRows.filter(x => x.clientCompanyKey.toLowerCase() === String(criteria.clientCompanyKey).toLowerCase());
      isDataFiltered = true;
    }

    if (criteria.programCode !== "") {
      filterRows = filterRows.filter(x => x.programsDescription.toLowerCase().indexOf(String(criteria.programCode).toLowerCase()) >= 0);
      isDataFiltered = true;
    }

    if (criteria.accountNumber !== "") {
      filterRows = filterRows.filter(x => x.accountsDescription !== "" && x.accountsDescription.indexOf(criteria.accountNumber) >= 0);
      isDataFiltered = true;
      criteria.statusId = -1;
    }

    if (criteria.expiredIn === "check") {
      filterRows = filterRows.filter(x => x.expiredIn.indexOf("Day") !== -1);
      isDataFiltered = true;
      criteria.statusId = -1;
    }

    if (criteria.accountStatus === "Yes" || criteria.accountStatus === "No") {
      filterRows = filterRows.filter(x => x.accountValidStatus === criteria.accountStatus)
      isDataFiltered = true;
    }
    else if (criteria.statusId === 100) {
      // filterRows = filterRows.filter(x => x.accountNumber === criteria.accountNumber)
      // isDataFiltered = true;
    }

    if (criteria.lockedId > 0) {
      if (criteria.lockedId === 1) {
        filterRows = filterRows.filter(x => !x.lockedIndicator);
        isDataFiltered = true;
      }
      else if (criteria.lockedId === 2) {
        filterRows = filterRows.filter(x => x.lockedIndicator);
        isDataFiltered = true;
      }
    }

    if (criteria.equipmentVendorId > -1) {
      filterRows = filterRows.filter(x => x.equipmentVendorId === criteria.equipmentVendorId);
      isDataFiltered = true;
    }

    if (criteria.postingStatusId > -1) {
      filterRows = filterRows.filter(x => x.postingStatusId === criteria.postingStatusId);
      isDataFiltered = true;
    }

    if (criteria.statusId !== -1) {
      if (criteria.statusId > 0) {
        if (criteria.statusId === 100) {
          criteria.statusId = 0;
          filterRows = filterRows.filter(x => [2, 3].indexOf(x.statusId) !== -1);
          isDataFiltered = true;
        }
        else {
          isDataFiltered = true;
          filterRows = filterRows.filter(x => x.statusId === criteria.statusId);
        }
      }
      else {
        filterRows = filterRows.filter(x => [1, 2, 3, 4].indexOf(x.statusId) >= 0)
      }
    }
    else {
      criteria.statusId = 0;
    }

    if (criteria.contractOrderNumber.trim().length > 0) {
      filterRows = filterRows.filter(x => x.contractOrderNumber.toLowerCase().indexOf(criteria.contractOrderNumber.trim().toLowerCase()) >= 0)
      isDataFiltered = true;
    }

    if (isDataFiltered) {
      setShowFilterOptions(true);
    }

    setSearchCriteria(criteria)
    setFilterContractOrders(filterRows);

    window.scrollTo(0, 0);
  }

  const getDefaultSearchCriteria = () => {
    return JSON.parse(JSON.stringify(defaultSearchCriteriaValue));
  }

  // Datagrid row clicked for edit contract order
  const handleDataGridClick = (params: GridRowParams, event: any) => {
    goToRoutePage("contractorders/" + params.row.contractOrderKey);
  };

  // Add new contract order
  const newContractOrder = () => {

    if (!canWriteContractOrder(userFunctionAccess)) {
      showAlert({ message: "Access Denied.", severity: "error" });
      goToRoutePage("home");
      return;
    }

    goToRoutePage("contractorders/" + referenceLookups.newRecordDefaultId);
  }

  // Get contract order
  const getData = async (criteria) => {

    setLoading(true);
    setContractOrders([]);
    setIssueSummaries([]);
    setPreOrderStatusSummaries([]);

    const apiResponse = await getContractOrders();

    if (apiResponse.success) {
      const rows: any = [];

      apiResponse.data.contractOrders.forEach((contractOrder, index) => {
        const programInfo = getProgramText(contractOrder.clientCompanyKey, contractOrder.programs);
        const accountInfo = getAccountText(contractOrder.clientCompanyKey, contractOrder.programs);;
        rows.push({ id: index, programsDescription: programInfo[0], programCount: programInfo[1], accountsDescription: accountInfo[0], accountCount: accountInfo[1], ...contractOrder });
      });

      setLookupValues(apiResponse.data.lookupValues);

      var tempStatusSummaries: TitleCountIdSummaryItem[] = [];

      tempStatusSummaries.push({ id: 1, title: "Draft", count: rows.filter(x => x.statusId === 1).length });
      tempStatusSummaries.push({ id: 2, title: "Available", count: rows.filter(x => x.statusId === 2).length });
      tempStatusSummaries.push({ id: 3, title: "Pause", count: rows.filter(x => x.statusId === 3).length });
      tempStatusSummaries.push({ id: 4, title: "Inactive", count: rows.filter(x => x.statusId === 4).length });

      setStatusSummaries(tempStatusSummaries);
      setIssueSummaries(apiResponse.data.issueSummaries);
      setPreOrderStatusSummaries(apiResponse.data.preOrderStatusSummaries);

      setPrograms(tempProgams.sort((a, b) => a.programName.localeCompare(b.programName)));
      setContractOrders(rows);
      if (criteria != null) {
        searchCriteriaChanged(rows, criteria)
      }
      else {
        searchCriteriaChanged(rows, searchCriteria)
      }

      setLoading(false);
    }
    else {
      showAlert({ message: apiResponse.errorMessage, severity: "error" });
      setLoading(false);
      return;
    }
  }

  // Convert base64 string to bytes
  const base64ToBytes = (base64) => {
    var s = window.atob(base64);
    var bytes = new Uint8Array(s.length);
    for (var i = 0; i < s.length; i++) {
      bytes[i] = s.charCodeAt(i);
    }
    return bytes;
  };

  // Download report
  const downloadReport = async () => {

    try {
      setLoading(true);
      await API.get("/api/contractOrders/matchbatch/export")
        .then(async (response) => {
          setLoading(false);

          if (response.data.content === null) {
            showAlert({ message: "Unable to download report.", severity: "error" });
            return;
          }

          var blob = new Blob([base64ToBytes(response.data.content)]);

          const fileName = "file.xlsx";
          if (navigator.msSaveBlob) {
            // IE 10+
            navigator.msSaveBlob(blob, fileName);
          } else {
            const link = document.createElement("a");
            // Browsers that support HTML5 download attribute
            if (link.download !== undefined) {
              const url = URL.createObjectURL(blob);
              link.setAttribute("href", url);
              link.setAttribute("download", fileName);
              link.style.visibility = "hidden";
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }
          }

          showAlert({ message: "Downloading report.", severity: "success" });

          return;
        })
        .catch((error) => {
          setLoading(false);
          showAlert({
            message: apiReponseError(error, "Failed to export enrollment pending file items."),
            severity: "error",
          });
        });
    } catch (e) {
      setLoading(false);
      showAlert({
        message: apiRequestError(e, "Something went wrong in our system."),
        severity: "error",
      });
    }
  };

  // Refresh contract orders
  const refresh = async () => {
    await getData(null)
  };

  // Get all programs (arrary) to string for grid list
  const getProgramText = (clientCompanyKey: string, data: ContractOrderProgram[]) => {
    let returnValue = "";
    let programCount = 0;
    let splitter = "";
    try {
      data.forEach((item) => {
        if (tempProgams.filter(x => x.programCode === item.programCode).length === 0) {
          tempProgams.push(item);
        }
        programCount++;
        returnValue += splitter + item.programCode + " - " + item.programName;
        splitter = " | ";
      });

      if (returnValue === "") {
        splitter = "";
        referenceLookups.programs.filter(program => program.clientCompanyKey.toLowerCase() === clientCompanyKey.toLowerCase()).forEach(program => {
          returnValue += splitter + program.programCode + " - " + program.programName;
          splitter = " | ";
          programCount++;
        });
      }
    }
    catch { }


    return [returnValue, programCount];

  }

  // Get all programs (arrary) to string for grid list
  const getAccountText = (clientCompanyKey: string, data: ContractOrderProgram[]) => {
    let returnValue = "";
    let accountCount = 0;
    let splitter = "";
    try {
      data.forEach((program) => {
        program.accounts.forEach((item) => {
          if (item.active) {
            returnValue += splitter + item.accountNumber + (item.folderId > 0 ? " - " + item.folderName : "");
            splitter = " | ";
            accountCount++;
          }
        });
      });


    }
    catch { }
    return [returnValue, accountCount];
  }

  const StyledMenuItem = withStyles((theme) => ({
    root: {
      "&:focus": {
        backgroundColor: theme.palette.secondary.main,
        "& .MuiListItemIcon-root, & .MuiListItemText-primary": {
          color: theme.palette.common.white,
        },
      },
    },
  }))(MenuItem);

  // Get distinct client company keys from contract orders for dropdown list
  const distinctClientCompanyKeys = () => {
    const uniqueClientCompanyKeys = [""];
    const uniqueClientCompanyKeys2 = [""];
    try {
      contractOrders.forEach((contractOrder) => {
        var clientCompanyKey = contractOrder.clientCompanyKey.toLowerCase();
        if (uniqueClientCompanyKeys.indexOf(clientCompanyKey) === -1) {
          uniqueClientCompanyKeys.push(clientCompanyKey.toLowerCase());
        }
      });
    }
    catch (e) { }

    referenceLookups.clientCompanies.forEach(clientCompany => {
      if (uniqueClientCompanyKeys.includes(clientCompany.clientCompanyKey.toLowerCase())) {
        uniqueClientCompanyKeys2.push(clientCompany.clientCompanyKey.toLowerCase())
      }
    });

    return uniqueClientCompanyKeys2.filter(x => x !== "");
  }

  useEffect(() => {
    setWindowTitle("Contract Orders");
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const initialPageAsync = async () => {
      checkUserAuthenticated(isUserAuthenticated);

      if (isUserAuthenticated) {
        setLoading(false);
      }

      if (!canReadContractOrder(userFunctionAccess)) {
        showAlert({ message: "Access Denied.", severity: "error" });
        goToRoutePage("home");
        return;
      }

      let tempSearchCriteria = JSON.parse(JSON.stringify(searchCriteria));
      try {
        const jsonString = window.localStorage.getItem("ContractOrderSerachForm");

        if (jsonString !== null) {
          tempSearchCriteria = JSON.parse(jsonString) as SearchCriteria;
        }
      } catch { }

      setShowDashboard(getShowDashboardValue("ShowContractOrderDashboard", true));

      setLoading(true);

      getData(tempSearchCriteria);

    };
    initialPageAsync();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserAuthenticated]);

  return (
    <React.Fragment>
      {loading ? (
        <SplashWait />
      ) : (
        <Screen>

          <Box className={pageStyles.root}>
            <Grid container spacing={3}>
              <Grid item xs={9}>
                <Box className={globalStyles.bottomSpacer}>
                  <Breadcrumbs aria-label="breadcrumb">
                    <Link color="inherit" onClick={() => goToRoutePage("home")} className={globalStyles.link}>
                      Home
                    </Link>
                    <Typography color="textPrimary">Contract Orders</Typography>
                  </Breadcrumbs>
                </Box>
              </Grid>
              <Grid item xs={3}>
                <Hidden xsDown>
                  <Box display="flex" flexDirection="row-reverse">

                    <IconButton onClick={() => { }}>
                      <Tooltip arrow title="User Manual">
                        <Link
                          variant="body2"
                          href={"./ContractOrderUserDocumentation.pdf"}
                          onClick={() => {
                            showAlert({
                              message: "Opening Contract Order Guide",
                              severity: "info",
                            });
                          }}
                          color="secondary"
                          download
                          target="blank"
                        >
                          <HelpIcon color="secondary" />
                        </Link>
                      </Tooltip>
                    </IconButton>

                    <IconButton onClick={() => refresh()}>
                      <Tooltip arrow title={"Refresh"}>
                        <RefreshIcon color="secondary" />
                      </Tooltip>
                    </IconButton>

                    {canWriteContractOrder(userFunctionAccess) ? (
                      <>
                        <IconButton onClick={() => newContractOrder()}>
                          <Tooltip arrow title={"Add new contract number"}>
                            <AddIcon color="secondary" />
                          </Tooltip>
                        </IconButton>
                      </>
                    ) : null}

                    <IconButton>
                      <Tooltip arrow title={"Download Match/Batch Report"}>
                        <GetAppToneIcon color="secondary" onClick={() => downloadReport()} />
                      </Tooltip>
                    </IconButton>

                    <IconButton onClick={() => setShowDashboard(toggleShowDashboard("ShowContractOrderDashboard", showDashboard))}>
                      <Tooltip arrow title={!showDashboard ? "Show Dashboard" : "Hide Dashboard"}>
                        {!showDashboard ? <DashboardOffIcon color="secondary" /> : <DashboardOnIcon color="secondary" />}
                      </Tooltip>
                    </IconButton>

                    {
                      !showFilterOptions ?
                        <IconButton onClick={() => setShowFilterOptions(!showFilterOptions)}>
                          <Tooltip arrow title={"Show/Hide Filter"}>
                            {
                              showFilterOptions ? <HideAllIcon color="secondary" /> : <ShowAllIcon color="secondary" />
                            }
                          </Tooltip>
                        </IconButton>
                        : null
                    }
                  </Box>
                </Hidden>
                <Hidden smUp>
                  <Box display="flex" justifyContent="flex-end" width={1}>
                    <Tooltip arrow title="Menu">
                      <Button color="secondary" aria-controls="fade-menu" aria-haspopup="true" onClick={handleAnchorElOptionsMenuClick}>
                        <MoreIcon />
                      </Button>
                    </Tooltip>
                  </Box>
                </Hidden>
                <Menu elevation={2} id="grid-menu" anchorEl={anchorElForOptionsMenu} keepMounted open={Boolean(anchorElForOptionsMenu)} onClose={handleAnchorElOptionsMenuClose}>
                  {
                    !showFilterOptions ?
                      <StyledMenuItem
                        onClick={(e) => {
                          setShowFilterOptions(!showFilterOptions);
                          handleAnchorElOptionsMenuClose();
                        }}
                      >
                        <ListItemIcon>
                          {
                            showFilterOptions ? <HideAllIcon fontSize="small" /> : <ShowAllIcon fontSize="small" />
                          }
                        </ListItemIcon>
                        <ListItemText primary={"Show/Hide Filter "} />
                      </StyledMenuItem>
                      : null
                  }

                  <StyledMenuItem
                    onClick={(e) => {
                      setShowDashboard(toggleShowDashboard("ShowContractOrderDashboard", showDashboard))
                      handleAnchorElOptionsMenuClose();
                    }}
                  >
                    <ListItemIcon>
                      {!showDashboard ? <DashboardOffIcon fontSize="small" /> : <DashboardOnIcon fontSize="small" />}
                    </ListItemIcon>
                    <ListItemText primary={!showDashboard ? "Show Dashboard" : "Hide Dashboard"} />
                  </StyledMenuItem>

                  <StyledMenuItem
                    onClick={(e) => {
                      downloadReport();
                      handleAnchorElOptionsMenuClose();
                    }}
                  >
                    <ListItemIcon>
                      <GetAppToneIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={"Download Match/Batch Report "} />
                  </StyledMenuItem>

                  {
                    canWriteContractOrder(userFunctionAccess) ? (
                      <StyledMenuItem
                        onClick={(e) => {
                          newContractOrder();
                          handleAnchorElOptionsMenuClose();
                        }}
                      >
                        <ListItemIcon>
                          <AddIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={"Add new contract number "} />
                      </StyledMenuItem>
                    )
                      : null
                  }

                  <StyledMenuItem
                    onClick={(e) => {
                      refresh();
                      handleAnchorElOptionsMenuClose();
                    }}
                  >
                    <ListItemIcon>
                      <RefreshIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={"Refresh "} />
                  </StyledMenuItem>

                  <StyledMenuItem
                    onClick={(e) => {
                      openExternalLink("./ContractOrderUserDocumentation.pdf");
                      handleAnchorElOptionsMenuClose();
                    }}
                  >
                    <ListItemIcon>
                      <HelpIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={"User Manual"} />
                  </StyledMenuItem>
                </Menu>
              </Grid>
            </Grid>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={12} md={showDashboard ? 8 : 12} lg={showDashboard ? 9 : 12}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    {
                      showFilterOptions ?
                        <>
                          <Grid container spacing={3}>
                            <Grid item xs={12} sm={6} md={6} lg={6} >
                              <FormControl className={globalStyles.formControl} fullWidth={true}>
                                <InputLabel shrink={true}>Client Company</InputLabel>
                                <Select
                                  required
                                  native
                                  labelId="client-company-key-label"
                                  id="clientCompanyKey"
                                  name="clientCompanyKey"
                                  value={searchCriteria.clientCompanyKey}
                                  onChange={(e) => {
                                    if (e !== null) {
                                      searchCriteriaChanged(contractOrders, { ...getDefaultSearchCriteria(), clientCompanyKey: e.target.value as string, programCode: "" });
                                    }
                                  }}
                                >
                                  <option value="">All</option>
                                  <option value={referenceLookups.combinedCompanyDefaultId} style={{ color: 'blue' }}>Combined Companies/Programs</option>

                                  {
                                    distinctClientCompanyKeys().map(key => (
                                      <>
                                        {
                                          referenceLookups.clientCompanies.filter(clientCompany => clientCompany.clientCompanyKey.toLowerCase() === key).map((clientCompany, index) => (
                                            <option value={clientCompany.clientCompanyKey.toLowerCase()} key={index}>
                                              {clientCompany.name} ({clientCompany.hsbPartnerId})
                                            </option>
                                          ))
                                        }

                                      </>
                                    ))
                                  }
                                </Select>
                              </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6} md={6} lg={6} >
                              <FormControl className={globalStyles.formControl} fullWidth={true}>
                                <InputLabel shrink={true}>Program</InputLabel>
                                <Select
                                  required
                                  native
                                  labelId="program-code-label"
                                  id="programCode"
                                  name="programCode"
                                  value={searchCriteria.programCode}
                                  onChange={(e) => {
                                    if (e !== null) {
                                      searchCriteriaChanged(contractOrders, { ...getDefaultSearchCriteria(), clientCompanyKey: "", programCode: e.target.value as string });
                                    }
                                  }}
                                >
                                  <option value="">All</option>
                                  {
                                    programs.map((program, index) => (
                                      <option value={program.programCode} key={index}>
                                        {program.programCode} {program.programName}
                                      </option>
                                    ))
                                  }


                                </Select>
                              </FormControl>
                            </Grid>

                            <Grid item xs={6} sm={6} md={4} lg={showDashboard ? 2 : 2} >
                              <FormControl className={globalStyles.formControl} fullWidth={true}>
                                <InputLabel shrink={true}>Equipment Vendor</InputLabel>
                                <Select
                                  required
                                  native
                                  labelId="equipmentVendorId-label"
                                  id="equipmentVendorId"
                                  name="equipmentVendorId"
                                  value={searchCriteria.equipmentVendorId}
                                  onChange={(e) => {
                                    if (e !== null) {
                                      searchCriteriaChanged(contractOrders, { ...searchCriteria, equipmentVendorId: Number(e.target.value) });
                                    }

                                  }}
                                >
                                  <option value={-1}></option>
                                  {
                                    lookupValues.vendors.map(vendor => (
                                      <option value={vendor.value}>{vendor.text}</option>
                                    ))
                                  }
                                </Select>
                              </FormControl>
                            </Grid>

                            <Grid item xs={6} sm={6} md={4} lg={showDashboard ? 2 : 2} >
                              <FormControl className={globalStyles.formControl} fullWidth={true}>
                                <InputLabel shrink={true}>Posting Status</InputLabel>
                                <Select
                                  required
                                  native
                                  labelId="postingStatusId-label"
                                  id="postingStatusId"
                                  name="postingStatusId"
                                  value={searchCriteria.postingStatusId}
                                  onChange={(e) => {
                                    if (e !== null) {
                                      searchCriteriaChanged(contractOrders, { ...searchCriteria, postingStatusId: Number(e.target.value) });
                                    }

                                  }}
                                >
                                  <option value={-1}></option>
                                  {
                                    lookupValues.postingStatus.map(status => (
                                      <option value={status.value}>{status.text}</option>
                                    ))
                                  }
                                </Select>
                              </FormControl>
                            </Grid>

                            <Grid item xs={6} sm={6} md={4} lg={showDashboard ? 2 : 2} >
                              <FormControl className={globalStyles.formControl} fullWidth={true}>
                                <InputLabel shrink={true}>Status</InputLabel>
                                <Select
                                  required
                                  native
                                  labelId="status-label"
                                  id="statusId"
                                  name="statusId"
                                  value={searchCriteria.statusId}
                                  onChange={(e) => {
                                    if (e !== null) {
                                      searchCriteriaChanged(contractOrders, { ...searchCriteria, statusId: Number(e.target.value) });
                                    }

                                  }}
                                >
                                  <option value="0">All</option>

                                  {
                                    lookupValues.orderStatus.map(status => (
                                      <option value={status.value}>{status.text}</option>
                                    ))
                                  }
                                </Select>
                              </FormControl>
                            </Grid>

                            <Grid item xs={6} sm={6} md={4} lg={showDashboard ? 2 : 2} >
                              <FormControl className={globalStyles.formControl} fullWidth={true}>
                                <InputLabel shrink={true}>Locked</InputLabel>
                                <Select
                                  required
                                  native
                                  labelId="lockedIndicator-label"
                                  id="lockedIndicator"
                                  name="lockedIndicator"
                                  value={searchCriteria.lockedId}
                                  onChange={(e) => {
                                    if (e !== null) {
                                      searchCriteriaChanged(contractOrders, { ...searchCriteria, lockedId: Number(e.target.value) });
                                    }
                                  }}
                                >
                                  <option value="0">All</option>
                                  <option value="1">Open</option>
                                  <option value="2">Locked</option>
                                </Select>
                              </FormControl>
                            </Grid>

                            <Grid item xs={6} sm={6} md={4} lg={showDashboard ? 2 : 2} >
                              <FormControl className={globalStyles.formControl} fullWidth={true}>
                                <InputLabel shrink={true}>Account</InputLabel>
                                <Select
                                  required
                                  native
                                  labelId="accountStatus-label"
                                  id="accountStatus"
                                  name="accountStatus"
                                  value={searchCriteria.accountStatus}
                                  onChange={(e) => {
                                    if (e !== null) {
                                      searchCriteriaChanged(contractOrders, { ...searchCriteria, accountStatus: String(e.target.value) });
                                    }
                                  }}
                                >
                                  <option value="">All</option>
                                  <option value="Yes">Exists</option>
                                  <option value="No">Not Found</option>
                                </Select>
                              </FormControl>
                            </Grid>

                            <Grid item xs={6} sm={6} md={4} lg={showDashboard ? 2 : 2} >
                              <TextField id="contractOrderNumber" label="Contract Order" value={searchCriteria.contractOrderNumber}
                                onChange={(e) => {
                                  if (e !== null) {
                                    searchCriteriaChanged(contractOrders, { ...getDefaultSearchCriteria(), contractOrderNumber: e.target.value as string });
                                  }
                                }} />
                            </Grid>

                            <Grid item xs={12}   >
                              <Box p={3} display="flex" justifyContent="center" alignContent="center">
                                <Button className={globalStyles.buttonDefault}
                                  onClick={() => {
                                    setShowFilterOptions(false)
                                  }} >
                                  <u>Hide</u>
                                </Button>

                                <Button className={globalStyles.buttonDefault}
                                  onClick={() => {
                                    searchCriteriaChanged(contractOrders, { ...getDefaultSearchCriteria() });
                                  }} >
                                  <u>Clear</u>
                                </Button>
                              </Box>
                            </Grid>
                          </Grid>
                          <Box p={1}></Box>
                        </>
                        : null
                    }
                  </Grid>
                  <Grid item xs={12}>
                    <Box className={pageStyles.datagridContainer} style={{ height: (showFilterOptions ? '64vh' : '80vh') }}>
                      <CustomDataGrid
                        uniqueGridCode={"ContractOrdersResult"}
                        columns={columns} rows={filterContractOrders}
                        showToolBar={true}
                        sortModel={[{ field: "clientCompanyName", sort: "asc" }, { field: "contractOrderNumber", sort: "asc" }]}
                        handleDataGridClick={handleDataGridClick} />
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
              {
                showDashboard ?
                  <Grid item xs={12} sm={12} md={4} lg={3}>
                    <Grid container spacing={3}>

                      <Grid item xs={12} sm={6} md={12} lg={12}>
                        <Card elevation={0} className={globalStyles.rightCardContainer}>
                          <CardHeader title="" subheader="STATUS SUMMARY" />
                          <CardContent>
                            <Grid container spacing={3}>
                              {
                                statusSummaries.map((statusSummary, index) => (

                                  <Grid item xs={6} key={index}>
                                    <Box display="flex" flexDirection="column" justifyItems='center' alignItems='center' textAlign="center"
                                      onClick={() => {
                                        searchCriteriaChanged(contractOrders, { ...getDefaultSearchCriteria(), statusId: statusSummary.id });
                                        setShowFilterOptions(true);
                                      }}
                                    >
                                      <Box>
                                        <Typography variant="h6" className="count"  >
                                          {statusSummary.count}
                                        </Typography>
                                      </Box>
                                      <Box>
                                        <Typography variant="caption" className={globalStyles.toggleFontColor}>
                                          <u>{statusSummary.title}</u>
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </Grid>
                                ))
                              }
                            </Grid>
                          </CardContent>
                        </Card>
                        <Box p="3">&nbsp;</Box>
                      </Grid>

                      <Grid item xs={12} sm={6} md={12} lg={12}>
                        <Card elevation={0} className={globalStyles.rightCardContainer}>
                          <CardHeader title="" subheader="PREORDER SUMMARY" />
                          <CardContent style={{ height: "250px", overflow: 'auto' }}>
                            <Grid container spacing={3}>
                              {
                                preOrderStatusSummaries.map((statusSummary, index) => (

                                  <Grid item xs={6} key={index}>
                                    <Box display="flex" flexDirection="column" justifyItems='center' alignItems='center' textAlign="center" >
                                      <Box>
                                        <Typography variant="h6" className="count"  >
                                          {statusSummary.count}
                                        </Typography>
                                      </Box>

                                      <Box>
                                        <Typography variant="caption" className={globalStyles.toggleFontColor}>
                                          {statusSummary.title}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </Grid>
                                ))
                              }
                            </Grid>
                          </CardContent>
                        </Card>
                        <Box p="3">&nbsp;</Box>
                      </Grid>

                      <Grid item xs={12} sm={6} md={12} lg={12}>
                        <Card elevation={0} className={globalStyles.rightCardContainer}>
                          <CardHeader title="" subheader="WARNINGS / ISSUES" />
                          <CardContent style={{ height: "250px", overflow: 'auto' }}>
                            {
                              issueSummaries.map((item, index) => (
                                <Box key={index} display="flex" flexDirection="column" justifyItems='center' alignItems='center' textAlign="center"
                                  onClick={() => {
                                    searchCriteriaChanged(contractOrders, { ...getDefaultSearchCriteria(), programCode: item.programCode, statusId: 100, postingStatusId: item.postingStatusId, equipmentVendorId: item.equipmentVendorId, accountNumber: item.accountNumber });
                                    setShowFilterOptions(true);
                                  }}
                                >
                                  <Box>
                                    <Typography variant="h6" className="count"  >
                                      {item.count}
                                    </Typography>
                                  </Box>
                                  <Box>
                                    <Typography variant="caption" className={globalStyles.toggleFontColor}>
                                      <u>
                                        {item.programName}<br />
                                        {item.accountNumber !== "" ? item.accountNumber : ""}
                                        {item.accountNumber !== "" ? <br /> : ""}
                                        {item.equipmentVendor}<br />
                                        {item.postingStatus}
                                      </u>
                                    </Typography>
                                    <Box p={1}></Box>
                                  </Box>
                                </Box>
                              ))
                            }

                            {
                              contractOrders.filter(x => x.accountValidStatus === "No").length > 0 ?
                                <Box display="flex" flexDirection="column" justifyItems='center' alignItems='center' textAlign="center"
                                  onClick={() => {
                                    searchCriteriaChanged(contractOrders, { ...getDefaultSearchCriteria(), accountStatus: "No" });
                                    setShowFilterOptions(true);
                                  }}>
                                  <Box>
                                    <Typography variant="h6" className="count"  >
                                      {contractOrders.filter(x => x.accountValidStatus === "No").length}
                                    </Typography>
                                  </Box>
                                  <Box>
                                    <Typography variant="caption" className={globalStyles.toggleFontColor}>
                                      <u>Account Missing In Meshify</u>
                                    </Typography>
                                    <Box p={1}></Box>
                                  </Box>
                                </Box>
                                : null
                            }

                            {
                              contractOrders.filter(x => x.expiredIn.indexOf("Day") !== -1).length > 0 ?
                                <Box display="flex" flexDirection="column" justifyItems='center' alignItems='center' textAlign="center"
                                  onClick={() => {
                                    searchCriteriaChanged(contractOrders, { ...getDefaultSearchCriteria(), expiredIn: "check" });
                                    setShowFilterOptions(true);
                                  }}>
                                  <Box>
                                    <Typography variant="h6" className="count"  >
                                      {contractOrders.filter(x => x.expiredIn.indexOf("Day") !== -1).length}
                                    </Typography>
                                  </Box>
                                  <Box>
                                    <Typography variant="caption" className={globalStyles.toggleFontColor}>
                                      <u>Expiring Soon</u>
                                    </Typography>
                                    <Box p={1}></Box>
                                  </Box>

                                </Box>
                                : null
                            }

                          </CardContent>
                        </Card>
                        <Box p="3">&nbsp;</Box>
                      </Grid>

                    </Grid>
                  </Grid>
                  : null
              }
            </Grid>

          </Box>
        </Screen>
      )
      }
    </React.Fragment >
  );
};

const LocalPageStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: 0,
    },
    datagridContainer: {
      minHeight: "450px",
      width: "100%",
      marginBottom: theme.spacing(2),
    },

  })
);

export default ManageContractOrders;
